<template>
  <div class="mt-4">
    <div class="bg-blue-50 border border-blue-100 rounded-lg px-4 py-4 mb-6">
      <div class="flex items-start gap-3">
        <div class="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
          <font-awesome-icon icon="fa-solid fa-file-invoice" />
        </div>
        <div>
          <h2 class="text-gray-800 font-semibold">Compras externas informativas</h2>
          <p class="text-sm text-gray-600 mt-1">
            Este módulo solo muestra las facturas externas recibidas por DGII. No crea inventario, no toca kardex y no modifica precios.
          </p>
        </div>
      </div>
    </div>

    <div class="flex justify-between flex-wrap md:flex-nowrap items-end mb-6 gap-4">
      <div class="flex items-end flex-wrap gap-4">
        <label class="block w-full md:w-auto">
          <span class="block text-gray-600 font-semibold mb-1 text-sm">Fec. de inicio</span>
          <input
            type="date"
            class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 resize-none"
            v-model="dateStart"
            @change="resetValuesAndGetData"
          />
        </label>
        <label class="block w-full md:w-auto">
          <span class="block text-gray-600 font-semibold mb-1 text-sm">Fec. término</span>
          <input
            type="date"
            class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 resize-none"
            v-model="dateEnd"
            @change="resetValuesAndGetData"
          />
        </label>
      </div>
    </div>

    <div class="rounded-lg overflow-hidden border">
      <div class="py-4 px-3 flex items-center justify-between gap-3">
        <select
          v-model="limitRegister"
          class="bg-white border py-2 text-sm px-3 text-gray-600 cursor-pointer rounded-lg outline-none min-w-[140px] font-semibold"
          @change="resetValuesAndGetData"
        >
          <option value="10">10 Registros</option>
          <option value="25">25 Registros</option>
          <option value="50">50 Registros</option>
          <option value="100">100 Registros</option>
          <option value="500">500 Registros</option>
        </select>
        <div class="w-full">
          <label>
            <input
              type="text"
              class="py-2 px-2 text-sm bg-white border outline-none rounded-lg shadow-sm text-gray-600 w-full"
              placeholder="Buscar por proveedor, RNC, e-NCF o receptor..."
              v-model="search"
              @keydown.enter="resetValuesAndGetData"
            />
          </label>
        </div>
      </div>

      <div class="w-full resize-x resize-generate" ref="widthNav" @resize="handleResize">
        <div class="overflow-x-auto">
          <table class="table-auto w-full">
            <TableHead :headers="header" />
            <LoadingTables v-if="loading" :columns="9" />
            <tbody class="text-gray-500 text-left border-t-[1px]" v-else>
              <tr
                v-for="(register, index) in registers"
                :key="index"
                class="hover:bg-blue-500 transition-colors hover:text-white capitalize font-semibold odd:bg-white even:bg-slate-50"
              >
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">
                  {{ register.razon_social_emisor }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap uppercase">
                  {{ register.rnc_emisor || '---' }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap uppercase bg-blue-50 text-blue-500">
                  {{ register.ecf }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">
                  {{ register.razon_social_receptor || '---' }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">
                  {{ register.details_count }} items
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">
                  {{ money() }} {{ format(register.subtotal) }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap font-bold">
                  {{ money() }} {{ format(register.total) }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">
                  {{ formatDateTime(register.fecha_emision || register.created_at) }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 text-right whitespace-nowrap flex justify-end">
                  <div class="relative group">
                    <button class="bg-blue-50 flex items-center justify-center w-8 h-8 rounded-md text-blue-500">
                      <font-awesome-icon icon="fa-solid fa-list-ul" />
                    </button>
                    <ul class="absolute top-full right-0 bg-white rounded-md shadow-xl shadow-blue-300 border-blue-50 border-2 overflow-hidden transition-all duration-300 transform scale-0 group-hover:scale-100 z-50">
                      <button
                        class="px-6 py-2 text-green-600 flex items-center hover:bg-green-50 whitespace-nowrap w-full"
                        @click="openDetail(register.id)"
                      >
                        <font-awesome-icon icon="fa-solid fa-eye" class="mr-2" />
                        Ver detalle
                      </button>
                      <button
                        class="px-6 py-2 text-blue-500 flex items-center hover:bg-blue-50 whitespace-nowrap w-full"
                        @click="printOnA4(register.id)"
                      >
                        <font-awesome-icon icon="fa-solid fa-print" class="mr-2" />
                        Imprimir compra externa
                      </button>
                    </ul>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <TablePagination
        :currentPage="currentPage"
        :total="totalPage"
        :next="next"
        :prev="prev"
      />
    </div>
  </div>

  <ExternalShoppingDetailModal
    v-if="showDetail"
    :key="externalShoppingId"
    :activeModal="toggleDetail"
    :id="externalShoppingId"
  />
</template>

<script>
import { onMounted, ref, watch, watchEffect } from "vue";
import { useRoute } from "vue-router";
import axios from "@/api";
import LoadingTables from "@/components/Loadings/LoadingTables";
import TableHead from "@/components/TablesComponents/TableHead";
import TablePagination from "@/components/TablesComponents/TablePagination.vue";
import ExternalShoppingDetailModal from "@/components/Shopping/External/ExternalShoppingDetailModal.vue";
import { warningActions, errorActions } from "@/alerts";
import { observer } from "@/observer";
import { money, format } from "@/publicjs/money";
import { formatDateTime } from "@/publicjs/formatDate";
import { printOnA4 } from "@/components/Shopping/External/PrintVoucher/PrintOnA4";

export default {
  name: "MainExternalShopping",
  components: {
    LoadingTables,
    TableHead,
    TablePagination,
    ExternalShoppingDetailModal,
  },
  setup() {
    const route = useRoute();
    const registers = ref([]);
    const loading = ref(true);
    const header = [
      [
        { title: "Proveedor externo" },
        { title: "RNC" },
        { title: "e-NCF" },
        { title: "Receptor" },
        { title: "Items" },
        { title: "Subtotal" },
        { title: "Total" },
        { title: "Fecha" },
        { title: "Acciones" },
      ],
    ];

    const currentPage = ref(1);
    const limitRegister = ref(10);
    const search = ref("");
    const totalPage = ref(0);
    const paginateData = ref(null);
    const dateStart = ref("");
    const dateEnd = ref("");
    const showDetail = ref(false);
    const externalShoppingId = ref(0);

    const getData = async () => {
      loading.value = true;
      const response = await axios
        .get(
          `compras-externas/?page=${currentPage.value}&per_page=${limitRegister.value}&search=${search.value}&date_start=${dateStart.value}&date_end=${dateEnd.value}`
        )
        .catch((error) => errorActions(error));

      if (!response) {
        loading.value = false;
        return;
      }

      if (response.data.data.length === 0) {
        warningActions("No encontramos compras externas con esos filtros.");
        observer.observe(document.querySelector(".swal2-container"), { box: "content-box" }, 10);
      }

      registers.value = response.data.data;
      paginateData.value = response.data;
      totalPage.value = paginateData.value.last_page;
      loading.value = false;
    };

    const resetValuesAndGetData = async () => {
      currentPage.value = 1;
      await getData();
    };

    const widthNav = ref(null);
    const handleResize = () => {
      const nav = document.querySelector("nav")?.offsetWidth || 0;
      watchEffect(() => {
        if (widthNav.value) {
          widthNav.value.style.width = `calc(100vw - ${nav + 42}px)`;
        }
      });
    };

    const next = async () => {
      totalPage.value = paginateData.value.last_page;
      if (currentPage.value <= totalPage.value - 1) currentPage.value++;
      await getData();
    };

    const prev = async () => {
      totalPage.value = paginateData.value.last_page;
      if (currentPage.value >= 2) currentPage.value--;
      await getData();
    };

    const toggleDetail = () => {
      showDetail.value = !showDetail.value;
    };

    const openDetail = (id) => {
      externalShoppingId.value = id;
      showDetail.value = true;
    };

    const openDetailFromRoute = () => {
      const routeId = Number(route.query.externalPurchaseId || 0);

      if (routeId > 0) {
        externalShoppingId.value = routeId;
        showDetail.value = true;
      }
    };

    onMounted(async () => {
      handleResize();
      window.addEventListener("resize", handleResize);
      await getData();
      openDetailFromRoute();
    });

    watch(
      () => [route.query.externalPurchaseId, route.query.externalNotification],
      () => {
        openDetailFromRoute();
      },
    );

    return {
      registers,
      loading,
      header,
      currentPage,
      limitRegister,
      search,
      totalPage,
      next,
      prev,
      getData,
      resetValuesAndGetData,
      widthNav,
      handleResize,
      money,
      format,
      formatDateTime,
      printOnA4,
      dateStart,
      dateEnd,
      showDetail,
      externalShoppingId,
      toggleDetail,
      openDetail,
    };
  },
};
</script>

<style scoped>
.group:hover .group-hover\:scale-100 {
  transform: scale(1);
}
</style>